import moment from "moment";
import { ACTIVITY_FIELD_KEY } from "../constants";
import { stripInterTagNewlines } from "../../../../common/normalizers/richText";

const UNSET_CHOICE_VALUE = '-1'
const DATE_INPUT_FORMATS = ["MM/DD/YYYY", "MM/DD/YYYY hh:mm A", "YYYY-MM-DD", moment.ISO_8601];

const TYPE_SUFFIX_TOKENS = new Set([
  'string', 'text', 'number', 'decimal', 'email',
  'date', 'datetime', 'datetimetimezome', 'datewithusertimezone', 'datewithtimezone',
  'singlechoicelist', 'multiplechoicelist',
  'checkbox', 'boolean',
  'zip', 'state', 'county', 'city', 'country',
  'address', 'name', 'timezone',
  'file', 'geopoint',
  'richtext', 'richtexteditor',
  'assignmentmethod', 'tag'
]);

const ACTIVITY_FILE_LOGICAL_NAME = 'FnolActFile';

const NON_TEMPLATE_KEYS = new Set([
  ACTIVITY_FIELD_KEY.TITLE,
  ACTIVITY_FIELD_KEY.OWNER,
  ACTIVITY_FIELD_KEY.DUE_DATE,
  ACTIVITY_FIELD_KEY.DUE_DATE_TZ,
  ACTIVITY_FIELD_KEY.DESCRIPTION,
  ACTIVITY_FIELD_KEY.SKILLSET,
  ACTIVITY_FIELD_KEY.ASSIGNMENT,
]);

export const ASSIGNEE_KEY_BY_METHOD = {
    teamMember: 'assignee_team_member',
    b2b: 'assignee_relationship_id',
    organization: 'assignee_organization'
}

export function stripHashSuffix(key) {
  if (typeof key !== 'string') return key;
  return key.replace(/_[0-9a-f]{6,}$/i, '');
}

function stripTypeSuffix(stablekey) {
  const parts = String(stablekey).split('_')
  if(parts.length > 2 && TYPE_SUFFIX_TOKENS.has(parts[parts.length - 1].toLowerCase())) {
    return parts.slice(0, -1).join('_')
  }
  return stablekey;
}

function extractTypeToken(key) {
  const stable = stripHashSuffix(key);
  const parts = String(stable).split('_');
  return parts.length >= 2 ? parts[parts.length - 1].toLowerCase() : '';
}

function toUnixSeconds(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }
  if (value instanceof Date) return Math.floor(value.getTime() / 1000);
  const parsed = moment(value, DATE_INPUT_FORMATS, true);
  if (parsed.isValid()) return parsed.unix();
  const loose = moment(value);
  return loose.isValid() ? loose.unix() : null;
}

function isDateKey(key) {
  const k = key.toLowerCase();
  if (k === ACTIVITY_FIELD_KEY.DUE_DATE.toLowerCase()) return true;
  if (k.includes("timezone")) return false;
  if (k === "duedate") return true;
  return k.includes("date");
}

function formatDate(key, value) {
  if (!value) return "";
  const fmt = key.toLowerCase().includes("datetime") ? TIME_FORMAT : DATE_FORMAT;
  return moment(value).format(fmt);
}

function isFileKey(key) {
  const k = key.toLowerCase();

  return /_file(_|$)/.test(k);
}

function normalizeFileEntry(file, isEdit) {
  if (!file || typeof file !== "object") return null;
  const guid = file.guid ?? file.FileGuid;
  if (!guid) return null;

  const caption = (file.caption ?? file.Caption ?? "").toString().trim();
  const name = file.name ?? file.FileName ?? ""

  const entry = {
    guid,
    name,
    caption
  };
  if (isEdit) {
    entry.file_id = file.file_id ?? 0;
  }
  return entry;
}

function normalizeFileValue(value, isEdit) {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr.map((file) => normalizeFileEntry(file, isEdit)).filter(Boolean);
}

function coerceValueByType(value, typeToken, isEdit) {
  if (
    typeToken === 'date' ||
    typeToken === 'datetime' ||
    typeToken === 'datetimetimezone' ||
    typeToken === 'datewithtimezone' ||
    typeToken === 'datewithusertimezone'
  ) {
    return toUnixSeconds(value);
  }
  if (typeToken === 'singlechoicelist' || typeToken === 'multiplechoicelist') {
    if (value === UNSET_CHOICE_VALUE || value === '' || value === null || value === undefined) {
      return null;
    }
    return value;
  }
  if (typeToken === 'checkbox') {
    return value === true || value === 'true' || value === 1 ? 'Yes' : 'No';
  }
  if (typeToken === 'file') {
    return normalizeFileValue(value, isEdit);
  }
  return value === undefined ? null : value;
}

function normalizeSkillset(value) {
 if(!Array.isArray(value)) return []

 return value
    .map(tag => (tag && (tag.text ?? tag.label ?? tag.value ?? tag.id)))
    .filter(s => typeof s === 'string' && s.length > 0)
}

function assignmentKeyValue(value) {
 if (!value || typeof value !== "object") return null;
 const key = ASSIGNEE_KEY_BY_METHOD[value.assignment_method]
 if(!key || value.assignMethodId === undefined || value.assignMethodId === null) return null
 return { key, value: value.assignMethodId}
}

function toNumericId(raw) {
  if(raw === undefined || raw === null || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function buildFieldKeyToLogicName(fields) {
  const map = {}

  if(!Array.isArray(fields)) return map

  fields.forEach((f) => {
    const logicalName = f?.mapFieldKey
    if(!logicalName) return 
    
    const candidates = [f?.fieldKey, f?.fieldName, f?.cf_field_name, f?.Name].filter(Boolean)
    candidates.forEach((k) => {map[k] = logicalName})
    candidates.forEach((k) => {
      const stable = stripHashSuffix(k)
      if(stable && !map[stable]) map[stable] = logicalName
    })
  })
  return map
}

export function buildActivityPayload({
  formData,
  templateId,
  workkardId,
  wrrId = 0,
  wrkdReopenSubreasonId = 0,
  activityId = null,
  isEdit = false,
  activityFields,
  tzList = [],
  fallbackDueDateTz = ''
}) {

  const templateFields = {};
  const unmappedKeys = [];
  const fieldKeyToLogicalName = buildFieldKeyToLogicName(activityFields)

  Object.entries(formData).forEach(([key, rawValue]) => {
    if(NON_TEMPLATE_KEYS.has(key)) return

    const stableKey = stripHashSuffix(key)
    const semanticName = fieldKeyToLogicalName[key] ?? fieldKeyToLogicalName[stableKey];
    const payloadKey = stripTypeSuffix(stableKey)
    const isFileField = extractTypeToken(key) === 'file' || isFileKey(key) || isFileKey(stableKey);
    const typeToken = isFileField ? 'file' : extractTypeToken(key)
    const coerced = coerceValueByType(rawValue, typeToken, isEdit);

    if (isFileField) {
      templateFields[ACTIVITY_FILE_LOGICAL_NAME] = coerced;
      return;
    }

    if (!semanticName) {
      unmappedKeys.push(key);
      templateFields[key] = coerced;
      return;
    }
    templateFields[semanticName] = coerced
  });

  const dueDateTzFormValue = formData[ACTIVITY_FIELD_KEY.DUE_DATE_TZ]
  const selectedTz = tzList.find((tz) => String(tz.id) === String(dueDateTzFormValue))
  const dueDateTz = dueDateTzFormValue ? (selectedTz?.mapped_time_zone || '') : (fallbackDueDateTz || '')

  const activity = {
    workkard_id: toNumericId(workkardId),
    title: formData[ACTIVITY_FIELD_KEY.TITLE] || '',
    description: stripInterTagNewlines(formData[ACTIVITY_FIELD_KEY.DESCRIPTION] || ''),
    due_date: toUnixSeconds(formData[ACTIVITY_FIELD_KEY.DUE_DATE]),
    due_date_tz: dueDateTz,
    owner_id: toNumericId(formData[ACTIVITY_FIELD_KEY.OWNER]),
    skillset: normalizeSkillset(formData[ACTIVITY_FIELD_KEY.SKILLSET]),
    template_type: templateId || formData.templateShortName || 'DEF',
    template_fields: templateFields
  };
  const assignment = assignmentKeyValue(formData[ACTIVITY_FIELD_KEY.ASSIGNMENT])
  if(assignment) activity[assignment.key] = assignment.value
  if (wrrId) {
    const numericWrrId = toNumericId(wrrId)
    if (numericWrrId != null) {
      activity.wrr_id = numericWrrId
      if (wrkdReopenSubreasonId) activity.wrkd_reopen_subreason_id = toNumericId(wrkdReopenSubreasonId)
    } else {
      activity.reopen_reason = String(wrrId)
      activity.reopen_sub_reason = wrkdReopenSubreasonId != null ? String(wrkdReopenSubreasonId) : ''
    }
  }
  if(isEdit && activityId) activity.activity_id = toNumericId(activityId)

  return { activity };
}